"use server";

import type { LoginState } from "@medplum/core";
import { cookies } from "next/headers";
import { z } from "zod";
import { createServerAction } from "@/lib/actions/create-server-action";
import { sessionCookieOptions } from "@/lib/auth/session-cookie-options";
import {
  MEDPLUM_COOKIE_NAME,
  SESSION_CANDIDATE_COOKIE_NAME,
} from "@/lib/auth/session-expired-cookie";

const InputSchema = z.custom<LoginState>();
const LOGIN_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 7;

export const setMedplumCookie = createServerAction({
  id: "set-medplum-cookie",
  description: "Sets the Medplum session cookie after login",
  inputSchema: InputSchema,
  handler: async ({ payload: activeLogin }) => {
    const serializedLogin = JSON.stringify(activeLogin);

    const cookieStore = await cookies();
    // Compare before writing: token rotation re-syncs the same login many times a session,
    // and a candidate marker that churns on every sync makes every other tab believe a new
    // user signed in. Opaque + rotated only on real change, so it leaks nothing about the token.
    const isNewLogin = cookieStore.get(MEDPLUM_COOKIE_NAME)?.value !== serializedLogin;

    cookieStore.set(
      MEDPLUM_COOKIE_NAME,
      serializedLogin,
      sessionCookieOptions({ isHttpOnly: true, maxAgeSeconds: LOGIN_COOKIE_MAX_AGE_SECONDS }),
    );

    if (isNewLogin) {
      cookieStore.set(
        SESSION_CANDIDATE_COOKIE_NAME,
        crypto.randomUUID(),
        sessionCookieOptions({ isHttpOnly: false, maxAgeSeconds: LOGIN_COOKIE_MAX_AGE_SECONDS }),
      );
    }
    return {};
  },
});
